import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { itemKind, type AccountRequest, type Bilingual, type DishRequest, type DishRequestItem } from "@al-life/types";
import { useNotifications } from "@/features/notifications/useNotifications";
import { Avatar } from "@/features/auth/Avatar";
import styles from "./MessageCenter.module.css";

/**
 * Admin-only message center. A bell button (next to the account menu) with an
 * unread count, opening a dropdown panel that aggregates:
 *   - Account requests — approve (creates the user) / dismiss.
 *   - Cooking orders — tap a row to open who / what / when, then mark done.
 *
 * Only rendered for admins (gated in Layout). No polling — refreshes on mount,
 * on open, and after each action (via the hook).
 */
export function MessageCenter() {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<DishRequest | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const n = useNotifications(true);

  useEffect(() => {
    if (!detail) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setDetail(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detail]);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) void n.reload(); // fresh on open
  }

  async function run(id: string, action: () => Promise<{ success: boolean; error?: string }>) {
    setBusyId(id);
    const res = await action();
    setBusyId(null);
    if (!res.success) window.alert(res.error ?? "Something went wrong.");
  }

  const pendingAccounts = n.accountRequests.filter((r) => r.status === "pending");
  const unhandledCooking = n.cookingRequests.filter((r) => !r.handled);
  const inboxSlug = unhandledCooking.find((r) => r.spaceSlug)?.spaceSlug;

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.bell}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={n.unread > 0 ? `Messages, ${n.unread} unread` : "Messages"}
      >
        <span aria-hidden="true">🔔</span>
        {n.unread > 0 && <span className={styles.badge}>{n.unread}</span>}
      </button>

      {open && (
        <>
          <div className={styles.backdrop} onClick={() => setOpen(false)} />
          <div className={styles.panel} role="menu">
            <div className={styles.panelHead}>
              <strong>Messages</strong>
              {n.status === "loading" && <span className={styles.dim}>Loading…</span>}
            </div>

            {n.error && <div className={styles.error}>{n.error}</div>}

            {pendingAccounts.length === 0 && unhandledCooking.length === 0 && n.status !== "loading" && (
              <p className={styles.empty}>You're all caught up 🎉</p>
            )}

            {/* Account requests */}
            {pendingAccounts.length > 0 && (
              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>Account requests</h4>
                {pendingAccounts.map((r) => (
                  <AccountRow
                    key={r.id}
                    req={r}
                    busy={busyId === r.id}
                    onApprove={() => run(r.id, () => n.approveAccount(r.id))}
                    onDismiss={() => run(r.id, () => n.dismissAccount(r.id))}
                  />
                ))}
              </section>
            )}

            {/* Cooking orders */}
            {unhandledCooking.length > 0 && (
              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>
                  Cooking orders
                  {inboxSlug && (
                    <Link to={`/${inboxSlug}/cooking`} className={styles.seeAll} onClick={() => setOpen(false)}>
                      inbox →
                    </Link>
                  )}
                </h4>
                {unhandledCooking.map((r) => (
                  <CookingRow key={r.id} req={r} onOpen={() => setDetail(r)} />
                ))}
              </section>
            )}
          </div>
        </>
      )}

      {detail && (
        <OrderDetail
          req={detail}
          busy={busyId === detail.id}
          onClose={() => setDetail(null)}
          onDone={() =>
            run(detail.id, async () => {
              const res = await n.setCookingHandled(detail.spaceSlug ?? "", detail.id, true);
              if (res.success) setDetail(null);
              return res;
            })
          }
        />
      )}
    </div>
  );
}

function AccountRow({
  req,
  busy,
  onApprove,
  onDismiss,
}: {
  req: AccountRequest;
  busy: boolean;
  onApprove: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className={styles.row}>
      <div className={styles.rowHead}>
        <Avatar avatarKey={req.avatarKey} size={28} />
        <div className={styles.rowText}>
          <span className={styles.rowName}>{req.name}</span>
          <span className={styles.rowSub}>{req.email}</span>
        </div>
      </div>
      {req.message && <p className={styles.rowMessage}>“{req.message}”</p>}
      <div className={styles.rowActions}>
        <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={onApprove} disabled={busy}>
          {busy ? "…" : "Approve"}
        </button>
        <button className={`${styles.btn} ${styles.btnGhost}`} onClick={onDismiss} disabled={busy}>
          Dismiss
        </button>
      </div>
    </div>
  );
}

function CookingRow({ req, onOpen }: { req: DishRequest; onOpen: () => void }) {
  const when = req.arrivalAt ? `Arriving ${formatWhen(req.arrivalAt)}` : `Placed ${formatWhen(req.createdAt)}`;
  const count = req.items.length === 1 ? "1 item" : `${req.items.length} items`;
  return (
    <button type="button" className={styles.orderBtn} onClick={onOpen}>
      <span className={styles.rowName}>{req.requesterName}</span>
      <span className={styles.rowSub}>
        {when} · {count}
      </span>
    </button>
  );
}

function OrderDetail({
  req,
  busy,
  onClose,
  onDone,
}: {
  req: DishRequest;
  busy: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const dishes = req.items.filter((item) => itemKind(item) !== "drink");
  const drinks = req.items.filter((item) => itemKind(item) === "drink");
  return (
    <div className={styles.detailBackdrop} onClick={onClose}>
      <div
        className={styles.detail}
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="order-detail-title" className={styles.detailTitle}>
          Order from {req.requesterName}
        </h2>
        <dl className={styles.facts}>
          <dt>Who</dt>
          <dd>{req.requesterName}</dd>
          <dt>Placed</dt>
          <dd>{formatWhen(req.createdAt)}</dd>
          {req.arrivalAt && (
            <>
              <dt>Arriving</dt>
              <dd>{formatWhen(req.arrivalAt)}</dd>
            </>
          )}
        </dl>

        <h3 className={styles.detailHeading}>What</h3>
        {dishes.length > 0 && <ItemGroup label="Food" mark="🍳" items={dishes} />}
        {drinks.length > 0 && <ItemGroup label="Drinks" mark="🥂" items={drinks} />}

        {req.message && <p className={styles.rowMessage}>“{req.message}”</p>}

        <div className={styles.rowActions}>
          <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={onClose}>
            Close
          </button>
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onDone} disabled={busy}>
            {busy ? "…" : "Mark done"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ItemGroup({ label, mark, items }: { label: string; mark: string; items: DishRequestItem[] }) {
  return (
    <div className={styles.itemGroup}>
      <p className={styles.itemGroupLabel}>
        {mark} {label}
      </p>
      <ul className={styles.itemList}>
        {items.map((item) => (
          <li key={`${item.kind ?? "dish"}:${item.dishId}`}>{itemTitle(item.dishName)}</li>
        ))}
      </ul>
    </div>
  );
}

function itemTitle(name: Bilingual): string {
  const en = name.en.trim();
  const cn = name.cn.trim();
  if (en && cn && en !== cn) return `${en} · ${cn}`;
  return en || cn || "Untitled";
}

/** Local wall-clock label for an order timestamp. Invalid input is shown as-is. */
function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
